import React, { useEffect, useState } from "react";
import { ArrowRight, ChevronDown, Sparkle, BookOpen, Compass, Eye, Wrench, Layers } from "lucide-react";
import Navigation from "../components/site/Navigation";
import Footer from "../components/site/Footer";
import Reveal from "../components/site/Reveal";
import LivingSectionTitle from "../components/site/LivingSectionTitle";
import OrientationResetCheckoutHost from "../components/site/OrientationResetCheckoutHost";
import ProductSchema from "../components/site/ProductSchema";
import AuthenticMomentumFAQ from "../components/site/AuthenticMomentumFAQ";
import ThisIsForYouIf from "../components/site/ThisIsForYouIf";
import { trackEvent } from "../lib/track";

/**
 * /start-here — The Guided Value Ladder.
 *
 * Single source of truth for "what step is right for me?". Renders the
 * full Authentic Momentum pathway as a connected chain of offer cards,
 * each following an identical schema (name, price, purpose, includes,
 * best-for, next-step bridge, primary CTA) so the visitor can scan
 * vertically and choose without re-decoding each section.
 *
 * Routing:
 *  - Snapshot / Book / Personal Action Plan cards link directly into the existing
 *    homepage anchors (book/blueprint) or the /frequency-snapshot route.
 *  - Clarity Reset Session routes to /orientation-reset/schedule (the
 *    pay-to-book gate; Stripe wiring happens server-side when ready).
 *  - Strategy + Build (Review, Map, Brief, Sprint) open the
 *    Consultation modal pre-filled with the offer context — these are
 *    invite/application based, so we capture intent rather than charge.
 */

// ---------------------------------------------------------------------------
// Offer schema (single source of truth for the ladder).
// ---------------------------------------------------------------------------
const STEPS = [
  {
    id: "snapshot",
    group: "Start Free",
    name: "Clarity Snapshot",
    price: "Free",
    icon: Sparkle,
    purpose:
      "A free reflective diagnostic that helps you see where your momentum is strong, where it is blocked, and what kind of next step may make sense.",
    included: [
      "Seven short reflection questions, about two minutes",
      "Personalized reading of your current pattern",
      "A pointer toward the next aligned move",
    ],
    bestFor:
      "First-time visitors. People circling a decision and unsure where they actually are.",
    when:
      "Choose this if you want to understand where you are before deciding anything. It is free, no email is required, and you see your result first.",
    nextStep:
      "Most readers move to the Book once the Snapshot names what they were already sensing.",
    cta: { label: "Take the Free Clarity Snapshot", href: "/frequency-snapshot", primary: true },
    testId: "ladder-step-snapshot",
    visualSlot: {
      id: "start-snapshot-signal",
      label: "The Signal",
      caption: "The first reading.",
      fit: "cover",
      prompt:
        "A clear glass prism on matte black, struck by a single warm gold beam that refracts into a geometric lattice of gold light on the surface below. Cinematic editorial still life, premium magazine composition.",
    },
  },
  {
    id: "book",
    group: "Enter the Framework",
    name: "Authentic Momentum · The Book",
    price: "$19.95",
    icon: BookOpen,
    purpose: "The original framework. Read it once and the noise organizes itself.",
    included: [
      "Full PDF, delivered instantly after purchase",
      "12 transmissions across the Authentic Momentum framework",
      "Foundational language used in every downstream offer",
    ],
    bestFor:
      "Builders, founders, and thoughtful operators who want the framework in their own hands first.",
    when:
      "Choose this if you want to sit with the philosophy and foundation at your own pace.",
    nextStep:
      "Pair the book with the Personal Action Plan to translate the framework into your specific path.",
    cta: { label: "Explore Authentic Momentum", href: "/book", primary: true },
    testId: "ladder-step-book",
    visualSlot: {
      id: "start-book-framework",
      label: "The Framework",
      caption: "The full method, in your hands.",
      fit: "cover",
      prompt:
        "The Authentic Momentum hardcover book on a black background, gold-embossed infinity emblem on the cover with luminous starburst at its center, surrounded by swirling gold particles. Cinematic editorial still life.",
    },
  },
  {
    id: "blueprint",
    group: "Turn Reflection Into Structure",
    name: "Authentic Momentum Personal Action Plan",
    price: "$49.99",
    icon: Compass,
    purpose: "A guided intake that converts your context into a structured next-move map.",
    included: [
      "Personal intake questionnaire (≈25 minutes)",
      "AI-assisted synthesis grounded in the framework",
      "A written Personal Action Plan Draft you can act on this week",
    ],
    bestFor:
      "Anyone who has the book and is ready to apply it to a specific decision, message, or move.",
    when:
      "Choose this if you want a clearer written next move without booking a live session.",
    nextStep:
      "If a hidden frame is still shaping the path, the Clarity Reset Session names it directly.",
    cta: { label: "Start the Personal Action Plan", href: "/?checkout=blueprint#blueprint", primary: true },
    testId: "ladder-step-blueprint",
    visualSlot: {
      id: "start-blueprint-outline",
      label: "The Personal Action Plan",
      caption: "Structure for what you are sensing.",
      fit: "contain",
      prompt:
        "A black vellum sheet etched with a fine gold sacred-geometry diagram of nested circles, intersecting lines, and orbital nodes. A black fountain pen with gold nib rests across the corner. Cinematic editorial still life.",
    },
  },
  {
    id: "orientation-reset",
    group: "Guided Support · The Gateway",
    name: "Clarity Reset Session",
    price: "$149",
    badge: "Live Guided Session",
    icon: Eye,
    purpose:
      "A paid guided session for people who want help interpreting what keeps circling, choosing the next practical move, and turning insight into structure.",
    included: [
      "30-minute live session with Cyrus",
      "Pre-session prep brief tailored to your Personal Action Plan",
      "Post-session signal note + access to deeper work",
    ],
    bestFor:
      "People who are circling a decision, transition, opportunity, or pattern and want help turning it into a practical next move. This is the gateway to all deeper paths.",
    when:
      "Choose this if you want live guided help making sense of what is circling, not just a written plan.",
    nextStep:
      "Once the frame is clear, the deeper paths open: the Structured Momentum Sprint and the Strategic Architecture Container.",
    cta: { label: "Book the Clarity Reset Session · $149", action: "orientation_reset", primary: true },
    testId: "ladder-step-orientation-reset",
    visualSlot: {
      id: "start-orientation-threshold",
      label: "The Threshold",
      caption: "The threshold to deeper work.",
      fit: "cover",
      prompt:
        "Two towering black stone monolith doors split by a vertical seam of brilliant gold light, reflecting onto a gold-veined marble floor. Cinematic editorial composition, premium magazine feel.",
    },
  },
  {
    id: "sprint",
    group: "Deeper Guided Implementation",
    name: "Structured Momentum Sprint",
    price: "By application",
    icon: Wrench,
    purpose:
      "A focused multi-week build that turns a clarified direction into structure: brand, offer, content system, AI workflow, or execution pathway.",
    included: [
      "A strategic map for the build",
      "Working sessions with Cyrus",
      "A practical, sequenced execution arc you can run",
    ],
    bestFor:
      "People ready to turn clarity into a short, focused execution arc.",
    when:
      "Choose this when the direction is clear and the build itself is the next step. Usually entered after a Clarity Reset Session.",
    nextStep:
      "Some sprints grow into long-term strategic support through the Strategic Architecture Container.",
    cta: { label: "Apply for a Sprint", action: "consult", surface: "sprint", primary: true },
    testId: "ladder-step-sprint",
    visualSlot: {
      id: "start-deeper-paths",
      label: "The Sprint",
      caption: "Where the work becomes tangible.",
      fit: "contain",
      prompt:
        "Four illuminated gold sigil nodes connected by a thin horizontal gold thread on matte black: a magnifying glass (Clarify), a tiered structure (Build), a thought bubble with lines (Express), and an upward bar chart with arrow (Scale).",
    },
  },
  {
    id: "architecture",
    group: "High-Level Strategic Support",
    name: "Strategic Architecture Container",
    price: "By invitation",
    icon: Layers,
    purpose:
      "A longer strategic partnership for people and ventures that need advisory, systems design, and structure across multiple moving parts.",
    included: [
      "Ongoing strategic advisory with Cyrus",
      "Systems, infrastructure, and AI-assisted design",
      "Structure across brand, offers, content, and operations",
    ],
    bestFor:
      "Founders, operators, and builders who need structure across multiple moving parts.",
    when:
      "Choose this when one decision is not the problem, and the whole architecture needs a steady strategic hand.",
    nextStep:
      "The container is the terminal surface of the public ladder. Everything beyond is private collaboration.",
    cta: { label: "Request Strategic Support", action: "consult", surface: "architecture", primary: true },
    testId: "ladder-step-architecture",
    visualSlot: {
      id: "start-architecture-container",
      label: "The Architecture",
      caption: "Structure across everything in motion.",
      fit: "cover",
      prompt:
        "An architectural lattice of fine champagne-gold lines forming a multi-level structural framework on matte black, interconnected planes and glowing nodes. Cinematic editorial still life.",
    },
  },
];

// Pretty index labels ("01", "02", ...) for the ladder rail.
const indexLabel = (i) => String(i + 1).padStart(2, "0");

// ---------------------------------------------------------------------------
// Chain Visual — horizontal on desktop, vertical on mobile.
// ---------------------------------------------------------------------------
const ChainVisual = () => (
  <div className="mt-12 sm:mt-16" data-testid="ladder-chain-visual">
    {/* Desktop chain */}
    <div className="hidden md:flex items-center justify-between gap-2 max-w-5xl mx-auto px-4">
      {STEPS.map((s, i) => (
        <React.Fragment key={s.id}>
          <a
            href={`#step-${s.id}`}
            data-testid={`chain-dot-${s.id}`}
            className="group flex flex-col items-center text-center min-w-0"
          >
            <span className="w-9 h-9 rounded-full border border-gold/40 flex items-center justify-center text-[10px] tracking-[0.3em] text-gold/80 group-hover:border-gold group-hover:text-gold group-hover:scale-110 transition-all duration-300 bg-black/40 backdrop-blur-sm">
              {indexLabel(i)}
            </span>
            <span className="mt-3 text-[10px] tracking-[0.32em] uppercase text-themed-muted group-hover:text-themed transition-colors max-w-[110px] leading-snug">
              {s.name.split("·")[0].trim()}
            </span>
          </a>
          {i < STEPS.length - 1 && (
            <span
              aria-hidden
              className="flex-1 h-px bg-gradient-to-r from-gold/30 via-gold/15 to-gold/30 -mt-7"
            />
          )}
        </React.Fragment>
      ))}
    </div>

    {/* Mobile chain */}
    <ol className="md:hidden relative pl-8 space-y-3 max-w-md mx-auto">
      <span
        aria-hidden
        className="absolute left-[15px] top-1 bottom-1 w-px bg-gradient-to-b from-gold/35 via-gold/15 to-gold/35"
      />
      {STEPS.map((s, i) => (
        <li key={s.id} className="relative">
          <a
            href={`#step-${s.id}`}
            data-testid={`chain-dot-mobile-${s.id}`}
            className="flex items-center gap-3"
          >
            <span className="absolute -left-8 w-7 h-7 rounded-full border border-gold/40 flex items-center justify-center text-[9px] tracking-[0.3em] text-gold/80 bg-black">
              {indexLabel(i)}
            </span>
            <span className="text-[12px] tracking-[0.18em] uppercase text-themed-muted">
              {s.name.split("·")[0].trim()}
            </span>
          </a>
        </li>
      ))}
    </ol>
  </div>
);

// ---------------------------------------------------------------------------
// StepVisualSlot — placeholder image frame that sits beside each OfferCard.
// Renders a gold-edged frame with the slot's caption + Grok-style prompt
// note that becomes the alt text. If the WebP at
// `/assets/start-here/<id>.webp` exists, it shows; otherwise the
// placeholder frame stands in until Cyrus drops the real art.
// ---------------------------------------------------------------------------
const StepVisualSlot = ({ slot }) => {
  if (!slot) return null;
  const src = `/assets/start-here/${slot.id}.webp`;
  const fit = slot.fit === "contain" ? "object-contain" : "object-cover";
  // Cover assets are photographic and want the dark vignette so the
  // caption stays legible at the bottom. Contain assets are diagrams
  // and need the full frame visible, so we soften the overlay to a
  // subtle bottom-anchored gradient only.
  const overlayClass =
    slot.fit === "contain"
      ? "absolute inset-0 bg-gradient-to-t from-black/85 via-black/15 to-transparent pointer-events-none"
      : "absolute inset-0 bg-gradient-to-tr from-black/80 via-black/30 to-transparent pointer-events-none";
  return (
    <figure
      data-testid={slot.id}
      className="relative aspect-[4/5] sm:aspect-[3/4] md:aspect-square w-full overflow-hidden border border-gold/25 bg-black/40 group"
    >
      {/* Native onError fallback — image hides if WebP not deployed yet,
          letting the placeholder layer below show through. */}
      <img
        src={src}
        alt={slot.prompt}
        loading="lazy"
        className={`absolute inset-0 w-full h-full ${fit} opacity-90 group-hover:opacity-100 transition-opacity duration-700`}
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
      <div className={overlayClass} />
      {/* Captions sit on a dark image gradient overlay regardless of
          page theme, so we anchor the typography to a light palette
          (ivory) in both modes. Theme-adaptive text-themed would turn
          dark charcoal here and disappear against the black/85 wash. */}
      <figcaption className="absolute inset-x-0 bottom-0 p-5 sm:p-6 flex flex-col gap-1 z-[1]">
        <span className="text-[10px] tracking-[0.4em] uppercase text-gold/90 drop-shadow-[0_1px_2px_rgba(0,0,0,0.55)]">
          {slot.label}
        </span>
        <span className="font-serif text-[#F5EFE2] text-base sm:text-lg leading-snug drop-shadow-[0_1px_3px_rgba(0,0,0,0.6)]">
          {slot.caption}
        </span>
      </figcaption>
      {/* Top-right corner tick — purely decorative gold accent */}
      <span
        aria-hidden
        className="absolute top-4 right-4 w-6 h-6 border-t border-r border-gold/60"
      />
    </figure>
  );
};

// ---------------------------------------------------------------------------
// Offer Card — uniform schema for every step on the ladder.
// ---------------------------------------------------------------------------
const OfferCard = ({ step, index, onCtaClick }) => {
  const [open, setOpen] = useState(false);
  const Icon = step.icon;
  const isLast = index === STEPS.length - 1;

  const handleCta = (e) => {
    if (step.cta.action === "consult") {
      e.preventDefault();
      onCtaClick(step);
      return;
    }
    if (step.cta.action === "orientation_reset") {
      e.preventDefault();
      onCtaClick(step);
      return;
    }
    trackEvent("start_here_cta_clicked", { step: step.id, label: step.cta.label });
  };

  const toggleIncludes = () => {
    const next = !open;
    setOpen(next);
    if (next) {
      trackEvent("start_here_details_expanded", { step: step.id });
    }
  };

  return (
    <Reveal>
      <article
        id={`step-${step.id}`}
        data-testid={step.testId}
        className="relative scroll-mt-32"
      >
        {/* Group eyebrow */}
        <div className="flex items-center gap-4 mb-5">
          <span className="text-[10px] tracking-[0.45em] uppercase text-gold/70">
            {indexLabel(index)} · {step.group}
          </span>
          <span aria-hidden className="flex-1 h-px bg-gold/15" />
        </div>

        <div className="glass-card p-7 sm:p-9 border-gold/20 hover:border-gold/40 transition-colors duration-500">
          {/* Header — icon + name + price stack vertically; the outer
              row layout already provides horizontal pairing with the
              visual slot, so no internal 12-col split is needed. */}
          <div className="space-y-6">
            <div>
              <div className="w-11 h-11 border border-gold/40 flex items-center justify-center mb-5 bg-black/40">
                <Icon className="text-gold" size={18} strokeWidth={1.3} />
              </div>
              <h3 className="font-serif text-2xl sm:text-3xl text-themed leading-tight">
                {step.name}
              </h3>
              <div className="mt-5 flex items-center gap-3 flex-wrap">
                <span
                  className="font-serif text-2xl text-gold"
                  data-testid={`${step.testId}-price`}
                >
                  {step.price}
                </span>
                {step.badge && (
                  <span className="text-[10px] tracking-[0.3em] uppercase px-2.5 py-1 border border-gold/35 text-gold/85 bg-black/30">
                    {step.badge}
                  </span>
                )}
              </div>
            </div>

            <div className="space-y-5">
              <p className="max-w-3xl text-white/85 leading-relaxed text-[15.5px] sm:text-[16.5px]">
                {step.purpose}
              </p>

              {/* What's included — collapsible */}
              <div className="border-t border-gold/15 pt-4">
                <button
                  type="button"
                  onClick={toggleIncludes}
                  data-testid={`${step.testId}-includes-toggle`}
                  aria-expanded={open}
                  className="flex items-center justify-between w-full text-left group"
                >
                  <span className="text-[11px] tracking-[0.35em] uppercase text-gold/85 group-hover:text-gold transition-colors">
                    What is included
                  </span>
                  <ChevronDown
                    size={16}
                    strokeWidth={1.3}
                    className={`text-gold/70 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                  />
                </button>
                <div
                  className={`grid transition-[grid-template-rows] duration-400 ease-out ${open ? "grid-rows-[1fr] mt-4" : "grid-rows-[0fr]"}`}
                >
                  <ul className="overflow-hidden space-y-2.5">
                    {step.included.map((item) => (
                      <li
                        key={item}
                        className="text-themed-muted text-[14px] leading-relaxed flex items-start gap-2.5"
                      >
                        <span aria-hidden className="text-gold mt-1.5 w-1 h-1 rounded-full bg-gold flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Best for */}
              <div>
                <div className="text-[10px] tracking-[0.4em] uppercase text-themed-muted mb-1.5">
                  Best for
                </div>
                <p className="text-themed-muted text-[14px] leading-relaxed">
                  {step.bestFor}
                </p>
              </div>

              {/* When to choose this */}
              {step.when && (
                <div>
                  <div className="text-[10px] tracking-[0.4em] uppercase text-gold/70 mb-1.5">
                    When to choose this
                  </div>
                  <p
                    data-testid={`${step.testId}-when`}
                    className="text-themed-muted text-[14px] leading-relaxed"
                  >
                    {step.when}
                  </p>
                </div>
              )}

              {/* How it connects to the next step */}
              {!isLast && (
                <div className="border-l border-gold/35 pl-4">
                  <div className="text-[10px] tracking-[0.4em] uppercase text-gold/80 mb-1.5">
                    How it connects
                  </div>
                  <p className="text-themed-muted text-[14px] leading-relaxed italic">
                    {step.nextStep}
                  </p>
                </div>
              )}

              {/* Primary CTA */}
              <div className="pt-2">
                <a
                  href={step.cta.href || "#"}
                  onClick={handleCta}
                  data-testid={`${step.testId}-cta`}
                  className="btn-gold inline-flex items-center gap-2 group"
                >
                  {step.cta.label}
                  <ArrowRight
                    size={15}
                    strokeWidth={1.5}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
      </article>
    </Reveal>
  );
};

// ---------------------------------------------------------------------------
// Page.
// ---------------------------------------------------------------------------
const StartHerePage = () => {
  useEffect(() => {
    document.title = "Start Here · Authentic Momentum";
    trackEvent("start_here_viewed");
  }, []);

  const handleCardCta = (step) => {
    if (step.cta.action === "orientation_reset") {
      // Fire the global Clarity Reset Session checkout host — same modal
      // used by FinalCTA so the visitor sees one consistent Stripe
      // experience regardless of entry point.
      trackEvent("start_here_cta_clicked", {
        step: step.id,
        label: step.cta.label,
        surface: "orientation_reset_checkout",
      });
      window.dispatchEvent(new CustomEvent("am:open-orientation-reset-checkout"));
      return;
    }
    if (step.cta.action === "consult") {
      // Sprint / Architecture are application or invitation based; open
      // the global consultation modal (listener lives in
      // ConsultationContext) with the offer surface attached.
      trackEvent("start_here_cta_clicked", {
        step: step.id,
        label: step.cta.label,
        surface: "consultation",
      });
      window.dispatchEvent(
        new CustomEvent("am:open-consultation", {
          detail: { surface: step.cta.surface, tier: step.id, label: step.name },
        })
      );
      return;
    }
    // All non-orientation_reset CTAs are `href` based and will let the
    // browser navigate naturally — we only fire tracking here.
    trackEvent("start_here_cta_clicked", {
      step: step.id,
      label: step.cta.label,
    });
  };

  return (
    <>
      <ProductSchema />
      <Navigation />
      <main
        data-testid="start-here-page"
        className="min-h-screen bg-themed noise-overlay pt-28 pb-24 relative overflow-hidden"
      >
        {/* Ambient gold field — keeps the page on-brand without competing
            with card content. */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-[0.18]"
          style={{
            background:
              "radial-gradient(ellipse 60% 40% at 50% 0%, rgba(212,175,55,0.35), transparent 60%)",
          }}
        />

        {/* HERO */}
        <section className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <Reveal>
            <p className="text-[11px] tracking-[0.5em] uppercase text-gold mb-6">
              Start Here
            </p>
            <LivingSectionTitle
              parts={[
                { text: "Start where you are." },
                { text: " " },
                { text: "Build from what is real.", italic: true },
              ]}
              align="center"
              variant="signal"
              className="text-4xl sm:text-5xl lg:text-6xl text-themed"
            />
            <p className="mt-7 text-white/85 leading-relaxed max-w-3xl mx-auto text-[15.5px] sm:text-[16.5px]">
              Authentic Momentum helps you locate the potential, pressure, and possibility inside your current season, then turn it into clarity, structure, and movement.
            </p>
            <p
              data-testid="start-here-pathway-line"
              className="mt-5 text-[11px] sm:text-[12px] tracking-[0.18em] uppercase text-gold/85"
            >
              Identity <span className="text-gold/50">→</span> Clarity{" "}
              <span className="text-gold/50">→</span> Structure{" "}
              <span className="text-gold/50">→</span> Execution{" "}
              <span className="text-gold/50">→</span> Proof
            </p>
          </Reveal>

          <Reveal delay={100}>
            <div
              data-testid="start-here-orientation-block"
              className="mt-10 border border-gold/25 bg-black/40 backdrop-blur-sm p-6 sm:p-8 max-w-3xl mx-auto"
            >
              <p className="text-[10px] tracking-[0.4em] uppercase text-gold/80 mb-3 text-center">
                How to use this page
              </p>
              <p className="text-themed-muted leading-relaxed text-[15px] sm:text-[15.5px] text-center">
                This page helps you choose the right entry point. Start with the free Clarity Snapshot if you are unsure. Choose the book if you want the philosophy. Choose the Personal Action Plan if you want a written next step. Choose the Clarity Reset Session if you want guided support.
              </p>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <ChainVisual />
          </Reveal>
        </section>

        {/* Audience archetypes - moved off the homepage during the
            compression pass. Founders / Builders / Creatives /
            Entrepreneurs cards continue the self-identification work
            for visitors who came this far. */}
        <ThisIsForYouIf />

        {/* LADDER — staggered visual + text blocks. Each step alternates
            image-left / image-right on desktop; mobile stacks
            visual-above-card. The visual is a placeholder frame that
            picks up `/assets/start-here/<slot>.webp` once the WebPs
            are produced. */}
        <section className="relative max-w-6xl mx-auto px-6 md:px-10 mt-20 sm:mt-24 space-y-16 md:space-y-24">
          {STEPS.map((step, i) => {
            const visualOnLeft = i % 2 === 0;
            return (
              <div
                key={step.id}
                data-testid={`start-row-${step.id}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center"
              >
                <div
                  className={`md:col-span-5 ${visualOnLeft ? "md:order-1" : "md:order-2"}`}
                >
                  <StepVisualSlot slot={step.visualSlot} />
                </div>
                <div
                  className={`md:col-span-7 ${visualOnLeft ? "md:order-2" : "md:order-1"}`}
                >
                  <OfferCard step={step} index={i} onCtaClick={handleCardCta} />
                </div>
              </div>
            );
          })}
        </section>

        {/* NOT SURE — Snapshot bridge */}
        <section className="relative max-w-3xl mx-auto px-6 md:px-10 mt-20 sm:mt-28">
          <Reveal>
            <div
              data-testid="start-here-uncertain-block"
              className="border border-gold/25 bg-black/40 backdrop-blur-sm p-8 sm:p-12 text-center"
            >
              <p className="text-[10px] tracking-[0.4em] uppercase text-gold/80 mb-3">
                Not sure where to start?
              </p>
              <h2 className="font-serif text-2xl sm:text-3xl text-themed leading-tight mb-4">
                Begin with the <span className="italic gold-gradient-text">signal.</span>
              </h2>
              <p className="text-themed-muted max-w-xl mx-auto leading-relaxed text-[15px]">
                If you are unsure, take the free Clarity Snapshot first. It is designed to help you see which path fits your current season. It takes about two minutes and no email is required.
              </p>
              <a
                href="/frequency-snapshot"
                data-testid="start-here-uncertain-cta"
                onClick={() =>
                  trackEvent("start_here_uncertain_cta_clicked")
                }
                className="btn-ghost-gold mt-7 inline-flex items-center gap-2 group"
              >
                Take the Free Clarity Snapshot
                <ArrowRight
                  size={14}
                  strokeWidth={1.5}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </a>
            </div>
          </Reveal>
        </section>

        {/* STILL TRYING TO UNDERSTAND WHERE YOU ARE? — three editorial
            bridges into the Journal that meet visitors who are not
            ready to step onto the ladder yet. Each link routes to a
            specific journal post chosen for the doorway it opens. */}
        <section
          className="relative max-w-5xl mx-auto px-6 md:px-10 mt-20 sm:mt-28"
          data-testid="start-here-journal-bridge"
        >
          <Reveal>
            <p className="text-[10px] tracking-[0.4em] uppercase text-gold/80 text-center mb-3">
              Still trying to understand where you are?
            </p>
            <h2 className="font-serif text-2xl sm:text-3xl text-themed text-center leading-tight mb-10 max-w-2xl mx-auto">
              Three pieces that have helped people{" "}
              <span className="italic gold-gradient-text">find the doorway.</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  slug: "ego-sounds-like-protection",
                  title:
                    "Your Ego Does Not Always Sound Like Pride. Sometimes It Sounds Like Protection.",
                  hint: "If the protective version of you has been deciding everything.",
                },
                {
                  slug: "finding-yourself-without-performance",
                  title:
                    "You Keep Trying to Find Yourself Without Losing the Performance.",
                  hint: "If the role you have been holding is starting to feel small.",
                },
                {
                  slug: "avoiding-grief-of-growth",
                  title:
                    "You Are Not Confused. You Are Avoiding the Grief of Outgrowing What Once Fit.",
                  hint: "If the in between has stretched longer than it should.",
                },
              ].map((p) => (
                <a
                  key={p.slug}
                  href={`/journal/${p.slug}`}
                  data-testid={`start-here-journal-${p.slug}`}
                  onClick={() =>
                    trackEvent("start_here_journal_bridge_clicked", { slug: p.slug })
                  }
                  className="glass-card p-6 border-gold/15 hover:border-gold/45 hover:-translate-y-1 transition-all duration-300 flex flex-col group"
                >
                  <span className="text-[10px] tracking-[0.3em] uppercase text-gold/70 mb-3">
                    Journal
                  </span>
                  <h3 className="font-serif text-base sm:text-lg text-themed leading-snug mb-3">
                    {p.title}
                  </h3>
                  <p className="text-themed-muted text-[13px] leading-relaxed flex-grow">
                    {p.hint}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-gold/85 group-hover:text-gold border-b border-gold/30 group-hover:border-gold pb-1 self-start transition-colors">
                    Read the piece <ArrowRight size={11} strokeWidth={1.5} />
                  </span>
                </a>
              ))}
            </div>
          </Reveal>
        </section>

        <AuthenticMomentumFAQ />
      </main>
      <OrientationResetCheckoutHost />
      <Footer />
    </>
  );
};

export default StartHerePage;
